import React, { useEffect, useRef, useState } from "react";
import { fmtDay, nf } from "./format";

const H = 230;
const M = { l: 34, r: 10, t: 10, b: 24 };

function niceMax(v) {
  const raw = Math.max(v, 4) / 4;
  const p = Math.pow(10, Math.floor(Math.log10(raw)));
  for (const m of [1, 2, 5, 10]) if (m * p >= raw) return m * p * 4;
  return raw * 4;
}

export default function SeriesChart({ series, lines }) {
  const ref = useRef(null);
  const [w, setW] = useState(640);
  const [hover, setHover] = useState(null);
  const [off, setOff] = useState({});

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const upd = () => setW(Math.max(el.clientWidth, 260));
    upd();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(upd);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const active = lines.filter(([k]) => !off[k]);
  const n = series.length;
  const max = niceMax(Math.max(1, ...series.flatMap((p) => active.map(([k]) => p[k] || 0))));
  const iw = w - M.l - M.r;
  const ih = H - M.t - M.b;
  const x = (i) => M.l + (n <= 1 ? iw / 2 : (i / (n - 1)) * iw);
  const y = (v) => M.t + ih - (v / max) * ih;
  const ticks = [0, 1, 2, 3, 4].map((i) => (max * i) / 4);
  const labelEvery = Math.max(1, Math.ceil(n / Math.max(2, Math.floor(iw / 70))));

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const px = e.clientX - r.left;
    const i = Math.round(((px - M.l) / iw) * (n - 1));
    setHover(Math.min(n - 1, Math.max(0, i)));
  };

  return (
    <div>
      <div className="an-legend">
        {lines.map(([k, label, color]) => (
          <button
            key={k}
            type="button"
            aria-pressed={!off[k]}
            onClick={() => setOff((o) => ({ ...o, [k]: !o[k] }))}
            style={{ opacity: off[k] ? 0.4 : 1 }}
          >
            <i style={{ background: color }} />
            {label}
          </button>
        ))}
      </div>
      <div className="an-chartwrap" ref={ref}>
        <svg className="an-chart" width={w} height={H} viewBox={`0 0 ${w} ${H}`} role="img" aria-label="График по дням" onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
          {ticks.map((t) => (
            <g key={t}>
              <line x1={M.l} x2={w - M.r} y1={y(t)} y2={y(t)} stroke="#e7edf7" />
              <text x={M.l - 6} y={y(t) + 4} textAnchor="end">{nf(Math.round(t * 10) / 10)}</text>
            </g>
          ))}
          {series.map((p, i) =>
            i % labelEvery === 0 ? (
              <text key={p.date} x={x(i)} y={H - 6} textAnchor="middle">{fmtDay(p.date)}</text>
            ) : null,
          )}
          {active.map(([k, , color]) => (
            <g key={k}>
              <polyline fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" points={series.map((p, i) => `${x(i)},${y(p[k] || 0)}`).join(" ")} />
              {n <= 31 && series.map((p, i) => (p[k] ? <circle key={i} cx={x(i)} cy={y(p[k])} r="2.5" fill={color} /> : null))}
            </g>
          ))}
          {hover != null && (
            <g>
              <line x1={x(hover)} x2={x(hover)} y1={M.t} y2={M.t + ih} stroke="#9db4d8" strokeDasharray="3 3" />
              {active.map(([k, , color]) => (
                <circle key={k} cx={x(hover)} cy={y(series[hover][k] || 0)} r="4" fill="#fff" stroke={color} strokeWidth="2" />
              ))}
            </g>
          )}
        </svg>
        {hover != null && (
          <div className="an-tip" style={{ left: Math.min(Math.max(x(hover), 70), w - 70), top: M.t + 40 }}>
            <b>{fmtDay(series[hover].date)}</b>
            {active.map(([k, label, color]) => (
              <div key={k}>
                <span style={{ color }}>●</span> {label}: {nf(series[hover][k] || 0)}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
